'use client'

import { createContext, useContext, useState, useCallback } from 'react'
import { track } from '@/lib/tracking'

const TopicContext = createContext(null)

export function TopicProvider({
  children,
  subtopics = [],
  allArticles = [],
  topicSlug = null,
}) {
  // Active section: null = show all, or { id, name, articles }
  const [activeSection, setActiveSection] = useState(null)

  // Highlighted article ID (for hover effects)
  const [highlightedArticleId, setHighlightedArticleId] = useState(null)

  // Bias filters: which bias categories to show
  const [biasFilters, setBiasFilters] = useState(['left', 'center', 'right'])

  // Articles modal state — the slide-in panel that lists a section's articles
  // (used on every breakpoint, triggered from a coverage angle's "View all").
  const [articlesModalOpen, setArticlesModalOpen] = useState(false)
  const [articlesModalArticles, setArticlesModalArticles] = useState([])
  const [articlesModalTitle, setArticlesModalTitle] = useState('')

  const filterBySection = useCallback((sectionId, sectionName, articles) => {
    setActiveSection({ id: sectionId, name: sectionName, articles })
  }, [])

  const filterBySectionAndBias = useCallback((sectionId, sectionName, articles, bias) => {
    setActiveSection({ id: sectionId, name: sectionName, articles })
    setBiasFilters([bias])
  }, [])

  const clearFilter = useCallback(() => {
    setActiveSection(null)
    setBiasFilters(['left', 'center', 'right'])
  }, [])

  // Filter by bias only (used by header BiasBars)
  const filterByBias = useCallback((bias) => {
    setActiveSection(null) // Clear section filter, show all articles
    setBiasFilters([bias])
  }, [])

  // Bias filter is a single-select "isolate": click a side to show ONLY it;
  // click the active side again to clear back to all three. The array shape is
  // kept (all three = "no filter", one = "isolated") so the downstream filter
  // logic (article's bias ∈ set) is unchanged. A side is considered "selected"
  // only when it's the sole entry — all three means nothing is highlighted.
  const selectBias = useCallback(
    (bias) => {
      setBiasFilters((prev) => {
        const clearing = prev.length === 1 && prev[0] === bias
        return clearing ? ['left', 'center', 'right'] : [bias]
      })
      track('bias_filter_selected', {
        bias:
          biasFilters.length === 1 && biasFilters[0] === bias ? 'clear' : bias,
        story_slug: topicSlug,
      })
    },
    [biasFilters, topicSlug]
  )

  const highlightArticle = useCallback((articleId) => {
    setHighlightedArticleId(articleId)
  }, [])

  const clearHighlight = useCallback(() => {
    setHighlightedArticleId(null)
  }, [])

  // Open the articles modal (slide-in panel) for a section's articles
  const openArticlesModal = useCallback(
    (articles, title, initialBias = null) => {
      setArticlesModalArticles(articles)
      setArticlesModalTitle(title)
      if (initialBias) {
        setBiasFilters([initialBias])
      } else {
        setBiasFilters(['left', 'center', 'right'])
      }
      setArticlesModalOpen(true)
      track('articles_panel_opened', {
        panel_title: title,
        article_count: articles?.length ?? 0,
        story_slug: topicSlug,
      })
    },
    [topicSlug]
  )

  const closeArticlesModal = useCallback(() => {
    setArticlesModalOpen(false)
  }, [])

  return (
    <TopicContext.Provider
      value={{
        activeSection,
        filterBySection,
        filterBySectionAndBias,
        filterByBias,
        clearFilter,
        biasFilters,
        selectBias,
        highlightedArticleId,
        highlightArticle,
        clearHighlight,
        subtopics,
        allArticles,
        topicSlug,
        // Articles modal (slide-in panel)
        articlesModalOpen,
        articlesModalArticles,
        articlesModalTitle,
        openArticlesModal,
        closeArticlesModal,
      }}
    >
      {children}
    </TopicContext.Provider>
  )
}

export function useTopicContext() {
  const context = useContext(TopicContext)
  if (!context) {
    throw new Error('useTopicContext must be used within a TopicProvider')
  }
  return context
}

// Optional version that returns null instead of throwing
export function useTopicContextOptional() {
  return useContext(TopicContext)
}
